import { createContext, useCallback, useContext, useMemo, useState } from 'react'
import type { PropsWithChildren } from 'react'
import {
  DISTRIBUTORS,
  type Distributor,
  type Order,
} from './fixtures'

/**
 * The checkout state the ported domain has no home for.
 *
 * `catalog`, `cart` and `config` are the three slices that came across (see
 * `docs/NOT_PORTED.md`). A chosen distributor, a delivery date, a payment
 * method and an order history are none of them, and there is
 * no backend behind any of it — so it lives here, in `web`, in one place, and
 * it is deliberately **not** in Redux. Putting it in the store would make it
 * look like domain state that a React Native shell could reuse, and it is not:
 * it is a placeholder until commerce-scs grows the endpoints.
 *
 * When those endpoints arrive, this context is what gets deleted.
 */
export interface DemoState {
  distributorId: string | null
  /**
   * The delivery date as the client system offered it — an opaque token,
   * echoed back verbatim to `setCheckoutRuleSession`. Kept here so payment
   * and confirmation can read the choice back; the server-side copy on the
   * session is what the order actually carries.
   */
  deliveryDate: string | null
  shippingMethodId: 'programada' | 'express' | 'pickup'
  /** The chosen payment token as the API offered it — `CASH`, `CREDIT`. */
  paymentMethodId: string | null
  orders: Order[]
  lastOrderId: string | null
  visitDay: string | null
  visitConfirmed: boolean
}

export interface DemoActions {
  chooseDistributor: (id: string) => void
  chooseDeliveryDate: (token: string | null) => void
  chooseShippingMethod: (id: DemoState['shippingMethodId']) => void
  choosePaymentMethod: (id: string | null) => void
  /** Records a placed order and returns its id, so the caller can route to it. */
  placeOrder: (order: Order) => void
  confirmVisitDay: (day: string) => void
}

export interface DemoContextValue extends DemoState, DemoActions {
  /** The chosen distributor, or the first one — screens always have one. */
  distributor: Distributor
}

const DemoContext = createContext<DemoContextValue | null>(null)

const EMPTY_STATE: DemoState = {
  distributorId: null,
  deliveryDate: null,
  shippingMethodId: 'programada',
  paymentMethodId: null,
  orders: [],
  lastOrderId: null,
  visitDay: null,
  visitConfirmed: false,
}

/**
 * `initial` exists for tests, and only for tests.
 *
 * A screen that renders differently once a choice has been made — a highlighted
 * distributor, a chosen payment method — cannot be tested through the provider
 * alone: the page's own state initialises before anything can dispatch into it.
 * Seeding is the alternative to exporting a setter nobody should call in
 * production, and it is read once, so a later change still has to go through
 * the actions.
 */
export const DemoProvider = ({
  initial,
  children,
}: PropsWithChildren<{ initial?: Partial<DemoState> }>) => {
  const [state, setState] = useState<DemoState>({ ...EMPTY_STATE, ...initial })

  const patch = useCallback(
    (next: Partial<DemoState>) => setState((current) => ({ ...current, ...next })),
    [],
  )

  /**
   * Records the order the server just created, so the confirmation screen can
   * name it — no endpoint reports back "the one you just placed". The id is the
   * server's: minting one here produced `YL-2026-0913`, which the order detail
   * then asked the API for and was rejected as not a 24-character hex id.
   */
  const placeOrder = useCallback((order: Order): void => {
    setState((current) => ({
      ...current,
      orders: [order, ...current.orders],
      lastOrderId: order.id,
      paymentMethodId: null,
    }))
  }, [])

  const value = useMemo<DemoContextValue>(
    () => ({
      ...state,
      distributor:
        DISTRIBUTORS.find((entry) => entry.id === state.distributorId) ?? DISTRIBUTORS[0]!,
      chooseDistributor: (distributorId) => patch({ distributorId }),
      chooseDeliveryDate: (deliveryDate) => patch({ deliveryDate }),
      chooseShippingMethod: (shippingMethodId) => patch({ shippingMethodId }),
      choosePaymentMethod: (paymentMethodId) => patch({ paymentMethodId }),
      confirmVisitDay: (visitDay) => patch({ visitDay, visitConfirmed: true }),
      placeOrder,
    }),
    [state, patch, placeOrder],
  )

  return <DemoContext.Provider value={value}>{children}</DemoContext.Provider>
}

/**
 * Throws without a provider rather than returning a default, for the same
 * reason `useStorefrontConfig` does: a screen silently running on a fabricated
 * empty checkout is worse than a screen that does not render.
 */
export const useDemo = (): DemoContextValue => {
  const value = useContext(DemoContext)
  if (!value) throw new Error('useDemo must be used inside <DemoProvider>')
  return value
}
